import React from 'react';
import { Download } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { BillingCycleUb04DownloadButton } from './BillingCycleUb04DownloadButton';
import { BillingCycle837EdiDownloadButton } from './BillingCycle837EdiDownloadButton';
import BillingCycleNoteButton from './BillingCycleNoteButton';
import type { BillingCycleNoteButtonProps } from './BillingCycleNoteButton';
import type { BillType } from '../../services/api/types.gen';
import {
  DEFAULT_BILL_TYPE,
  isInvoiceBilled,
} from '../../constants/insuranceBilling';

/**
 * Unified right-aligned Actions cell shared by every Billing Manager tab.
 * `leading` holds any tab-specific actions (Preview, Edit, Send/Mark-as-Sent, …)
 * rendered before the shared Download dropdown and Notes button so every row
 * exposes the same Actions column. Draft tabs (Ready / Not Ready) have no claim
 * to download yet and pass `showDownload={false}`.
 */
export const BillingActionsCell: React.FC<{
  billingCycleId: string;
  noteProps: BillingCycleNoteButtonProps;
  leading?: React.ReactNode;
  showDownload?: boolean;
  /** An invoice claim never goes out electronically, so no 837. */
  billType?: BillType;
}> = ({
  billingCycleId,
  noteProps,
  leading,
  showDownload = true,
  billType = DEFAULT_BILL_TYPE,
}) => (
  <div className='flex items-center justify-end gap-0.5'>
    {leading}
    {showDownload && (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon' aria-label='Download claim'>
                  <Download className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='min-w-[208px] p-1'>
                <DropdownMenuLabel className='text-xs text-muted-foreground'>
                  Download claim
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <BillingCycleUb04DownloadButton
                  billingCycleId={billingCycleId}
                  label='Print PDF (UB-04)'
                />
                {!isInvoiceBilled(billType) && (
                  <BillingCycle837EdiDownloadButton
                    billingCycleId={billingCycleId}
                    label='Download EDI (837I)'
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Download claim (PDF / EDI)
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
    <BillingCycleNoteButton {...noteProps} />
  </div>
);
